import { useEffect, useRef, useState } from 'react';

import { Popover, useMediaQuery, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';

import { CAST_AI } from '@cast/constants';
import {
  Button,
  Card,
  Chip,
  Icons,
  IconButton,
  Stack,
  Typography,
  tokens,
  Box,
  notify,
} from '@cast/design-system';
import {
  useChatbotState,
  useIsOpsPilotEnabled,
} from '@cast/features-organization-shared-opspilot';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import {
  useDoesReportingMonetizationApply,
  useShouldLimitConsoleForReporting,
} from '@cast/shared-trial-ui';
import { toggleGettingStartedPylonOffset } from '@cast/utils';

import { LogoSmallSvg } from 'assets/svg';
import { Link } from 'components/router';
import { useShouldDeferOnboardingChecklistOpen } from 'main-layout-op/_hooks/useRedesignAnnouncement/useShouldDeferOnboardingChecklistOpen';

import {
  getNextIncompleteTaskId,
  getOnboardingCompletionPercent,
  getOnboardingProgressLabel,
  getRemainingRecommendedCount,
  shouldShowOnboardingWizardButton,
} from './_utils';
import { OnboardingTaskId } from './taskIds';
import type { OnboardingTask } from './types';
import { useOnboardingWizardDismiss } from './useOnboardingWizardDismiss';
import { useOnboardingWizardOpenRequestId } from './onboardingWizardOpenRequest';
import { useOnboardingWizardTasks } from './useOnboardingWizardTasks';

type InnerProps = {
  tasks: OnboardingTask[];
  remainingRecommendedCount: number;
  onDismissForever: () => void;
};

const taskRowSx = (isNext: boolean, index: number) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  p: 12,
  textDecoration: 'none',
  color: tokens.theme.fg.structural.primary,
  bgcolor: isNext ? tokens.theme.bg.selection.primarySubtle : undefined,
  borderTop:
    index > 0
      ? `1px solid ${tokens.theme.border.structural.neutral}`
      : undefined,
  cursor: 'pointer',
  '&:hover': {
    bgcolor: isNext
      ? tokens.theme.bg.selection.primarySubtleHover
      : tokens.theme.bg.structural.surfaceRaised,
    color: tokens.theme.fg.structural.primary,
  },
});

const OnboardingWizardButtonInner = ({
  tasks,
  remainingRecommendedCount,
  onDismissForever,
}: InnerProps) => {
  const deferOpen = useShouldDeferOnboardingChecklistOpen();
  const [isOpen, setIsOpen] = useState(!deferOpen);
  // Callback ref → state so Popover re-renders with a real anchor (ref.current is null on first paint).
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isFirstPathnameEffect = useRef(true);
  const hasAutoOpenedAfterAnnouncement = useRef(!deferOpen);
  const lastHandledOpenRequestId = useRef(0);
  const openRequestId = useOnboardingWizardOpenRequestId();
  const { pathname } = useLocation();
  const limitReportingConsole = useShouldLimitConsoleForReporting();
  const { currentOrganization } = useOrganizations();
  const { open } = useChatbotState();
  const { opsPilotStatus, onTryOpsPilot, tryOpsPilotDisabledTooltip } =
    useIsOpsPilotEnabled();

  const close = () => setIsOpen(false);
  const progressPercent = getOnboardingCompletionPercent(tasks);
  const progressLabel = getOnboardingProgressLabel(progressPercent);
  const nextIncompleteTaskId = getNextIncompleteTaskId(tasks);
  const shouldPulse = !isOpen && remainingRecommendedCount > 0;

  useEffect(() => {
    toggleGettingStartedPylonOffset(true);
    return () => toggleGettingStartedPylonOffset(false);
  }, []);

  const handleAskOpsPilotClick = () => {
    // For child orgs without enterprise management access there is nowhere to
    // navigate, so don't close the wizard and leave the user stranded.
    if (opsPilotStatus === 'off' && tryOpsPilotDisabledTooltip) {
      return;
    }
    close();
    if (opsPilotStatus === 'on') {
      open(currentOrganization?.id ?? 'default');
      return;
    }
    if (opsPilotStatus === 'off') {
      onTryOpsPilot();
    }
  };

  useEffect(() => {
    if (openRequestId > lastHandledOpenRequestId.current) {
      lastHandledOpenRequestId.current = openRequestId;
      if (deferOpen) {
        // Open once the redesign announcement dialog closes.
        hasAutoOpenedAfterAnnouncement.current = false;
      } else {
        setIsOpen(true);
      }
    }
  }, [openRequestId, deferOpen]);

  useEffect(() => {
    if (deferOpen) {
      setIsOpen(false);
      return;
    }
    if (!hasAutoOpenedAfterAnnouncement.current) {
      hasAutoOpenedAfterAnnouncement.current = true;
      setIsOpen(true);
    }
  }, [deferOpen]);

  useEffect(() => {
    // Open by default on first mount; only close on subsequent navigations.
    if (isFirstPathnameEffect.current) {
      isFirstPathnameEffect.current = false;
      return;
    }
    setIsOpen(false);
  }, [pathname]);

  const isChecklistOpen = isOpen && !deferOpen && Boolean(anchorEl);

  return (
    <Box
      sx={{
        position: 'fixed',
        right: 24,
        bottom: 24,
        zIndex: (theme) => theme.zIndex.drawer - 1,
      }}
      data-testid="onboarding-wizard-fab"
    >
      <Chip
        ref={setAnchorEl}
        size="lg"
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen((open) => !open)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsOpen((open) => !open);
          }
        }}
        startIcon={<LogoSmallSvg width={14} height={15} />}
        endIcon={
          <Box
            component="span"
            data-testid="onboarding-wizard-remaining-badge"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 20,
              height: 20,
              px: 6,
              borderRadius: tokens.theme.radius.full,
              bgcolor: tokens.theme.fg.action.onPrimary,
              color: tokens.theme.bg.action.primary,
              typography: 'P10B',
              lineHeight: 1,
            }}
          >
            {progressLabel}
          </Box>
        }
        testId="onboarding-wizard-trigger"
        sx={{
          cursor: 'pointer',
          borderRadius: tokens.theme.radius.full,
          bgcolor: tokens.theme.bg.action.primary,
          color: tokens.theme.fg.action.onPrimary,
          boxShadow: tokens.theme.shadows.elevation.medium,
          '@keyframes onboardingWizardPulse': {
            '0%': {
              boxShadow: `0 0 0 0 color-mix(in srgb, ${tokens.theme.bg.action.primary} 40%, transparent), ${tokens.theme.shadows.elevation.medium}`,
            },
            '100%': {
              boxShadow: `0 0 0 8px color-mix(in srgb, ${tokens.theme.bg.action.primary} 0%, transparent), ${tokens.theme.shadows.elevation.medium}`,
            },
          },
          ...(shouldPulse
            ? {
                animation: 'onboardingWizardPulse 1.8s ease-out infinite',
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
              }
            : {}),
        }}
      >
        Getting started
      </Chip>

      <Popover
        open={isChecklistOpen}
        anchorEl={anchorEl}
        onClose={close}
        // Negative vertical offset = gap above the FAB (mb on paper is ignored by positioning)
        anchorOrigin={{ vertical: -8, horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        disableScrollLock
        slotProps={{
          paper: {
            sx: {
              borderRadius: tokens.theme.radius.lg,
              overflow: 'hidden',
              width: 360,
              maxWidth: 'calc(100vw - 48px)',
              boxShadow: tokens.theme.shadows.elevation.high,
              border: `1px solid ${tokens.theme.border.structural.neutral}`,
              borderTop: `3px solid ${tokens.theme.bg.action.primary}`,
              bgcolor: tokens.theme.bg.structural.surface,
              color: tokens.theme.fg.structural.primary,
            },
          },
        }}
      >
        <Card
          sx={{
            p: 0,
            border: 'none',
            boxShadow: 'none',
            bgcolor: tokens.theme.bg.structural.surface,
            color: tokens.theme.fg.structural.primary,
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            gap={8}
            sx={{
              bgcolor: tokens.theme.bg.structural.surfaceRaised,
              borderBottom: `1px solid ${tokens.theme.border.structural.neutral}`,
              p: 16,
            }}
          >
            <Stack direction="row" alignItems="flex-start" gap={12}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: tokens.theme.radius.md,
                  bgcolor: tokens.theme.bg.structural.surface,
                  border: `1px solid ${tokens.theme.border.structural.neutral}`,
                  color: tokens.theme.fg.action.primary,
                  flexShrink: 0,
                }}
              >
                <LogoSmallSvg width={16} height={18} />
              </Box>
              <Stack gap={4}>
                <Typography
                  variant="P14M"
                  sx={{ color: tokens.theme.fg.structural.primary }}
                >
                  Getting started
                  {progressLabel ? ` · ${progressLabel}` : ''}
                </Typography>
                <Typography
                  variant="P12R"
                  sx={{ color: tokens.theme.fg.structural.secondary }}
                >
                  A few recommended steps to help you get the most out of{' '}
                  {CAST_AI}.
                  {limitReportingConsole
                    ? ' Custom OpsPilot questions require a paid plan — suggested questions still work.'
                    : ''}
                </Typography>
              </Stack>
            </Stack>
            <IconButton
              size="sm"
              variant="ghost"
              aria-label="Collapse setup guide"
              onClick={close}
            >
              <Icons.CaretDown />
            </IconButton>
          </Stack>

          <Stack
            gap={12}
            sx={{
              p: 12,
              bgcolor: tokens.theme.bg.structural.surface,
              color: tokens.theme.fg.structural.primary,
            }}
          >
            <Stack
              sx={{
                border: `1px solid ${tokens.theme.border.structural.neutral}`,
                borderRadius: tokens.theme.radius.md,
                overflow: 'hidden',
                bgcolor: tokens.theme.bg.structural.surface,
              }}
            >
              {tasks.map((task, index) => {
                const isNext = task.id === nextIncompleteTaskId;
                const isAskOpsPilot = task.id === OnboardingTaskId.AskOpsPilot;
                const rowContent = (
                  <>
                    <Stack direction="row" alignItems="center" gap={8}>
                      {task.checked ? (
                        <Icons.CheckCircle
                          size={20}
                          weight="fill"
                          color={tokens.theme.fg.status.success}
                        />
                      ) : (
                        <Icons.CircleDashed
                          size={20}
                          color={
                            isNext
                              ? tokens.theme.fg.action.primary
                              : tokens.theme.fg.structural.secondary
                          }
                        />
                      )}
                      <Typography
                        variant={isNext ? 'P14M' : 'P14R'}
                        sx={{ color: tokens.theme.fg.structural.primary }}
                      >
                        {task.label}
                      </Typography>
                    </Stack>
                    <Icons.ArrowRight
                      size={16}
                      color={
                        isNext
                          ? tokens.theme.fg.action.primary
                          : tokens.theme.fg.structural.secondary
                      }
                    />
                  </>
                );

                if (isAskOpsPilot) {
                  return (
                    <Box
                      key={task.id}
                      role="button"
                      tabIndex={0}
                      onClick={handleAskOpsPilotClick}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleAskOpsPilotClick();
                        }
                      }}
                      sx={taskRowSx(isNext, index)}
                      data-testid={`onboarding-wizard-task-${task.id}`}
                    >
                      {rowContent}
                    </Box>
                  );
                }

                return (
                  <Box
                    key={task.id}
                    component={Link}
                    to={task.href}
                    onClick={close}
                    sx={taskRowSx(isNext, index)}
                    data-testid={`onboarding-wizard-task-${task.id}`}
                  >
                    {rowContent}
                  </Box>
                );
              })}
            </Stack>

            <Button
              fullWidth
              size="sm"
              variant="text"
              onClick={() => {
                onDismissForever();
                close();
                notify.info('You can find Getting started again under Help.');
              }}
              testId="onboarding-wizard-dismiss"
            >
              Dismiss guide
            </Button>
          </Stack>
        </Card>
      </Popover>
    </Box>
  );
};

export const OnboardingWizardButton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { pathname } = useLocation();
  const { hideForever, forceVisible, dismissForever } =
    useOnboardingWizardDismiss();
  const { tasks, isLoading } = useOnboardingWizardTasks();
  const monetizationApplies = useDoesReportingMonetizationApply();

  const remainingRecommendedCount = getRemainingRecommendedCount(tasks);
  const isOnboardingRoute = pathname.startsWith('/onboarding');

  if (
    !shouldShowOnboardingWizardButton({
      isMobile,
      hideForever,
      isLoading,
      remainingRecommendedCount,
      isOnboardingRoute,
      monetizationApplies,
      forceVisible,
    })
  ) {
    return null;
  }

  return (
    <OnboardingWizardButtonInner
      tasks={tasks}
      remainingRecommendedCount={remainingRecommendedCount}
      onDismissForever={dismissForever}
    />
  );
};
